import { uniq } from 'lodash-es';
import { api } from '@/api';
import { Employee, EmployeeAvailabilityData, EmployeeComposed, EmployeeRole } from '@/api/apis/resources';
import { queryClient } from '@/queries';
import { employeeQueries } from '@/queries/employee';
import { homeLocationQueries } from '@/queries/homelocation';
import { hubQueries } from '@/queries/hub';
import { airportsQuery } from '@/queries/airports';
import { Hub } from '@/api/apis/facilities';
import { getTXCentersByIds, txCenterQueries } from '../tx-centers';
import { getOpoOrganizationsByIds, opoQueries } from '../opo';

export const getEmployeesByIds = async (employeeList: string[]) => {
  const employeeIdsSet = new Set(employeeList);
  const employeeIds = Array.from(employeeIdsSet);

  if (employeeIds.length) {
    return api.resource.getEmployeeList({
      employeeIds: employeeIds.join(','),
      pageIndex: 0,
      itemsPerPage: employeeIds.length,
    });
  }

  return null;
};

export const seedEmployeeFromList = (list: Employee[] = []) => {
  list.forEach((employee) => {
    queryClient.setQueryData(employeeQueries.item(employee.id).queryKey, employee);
  });
};

export const seedComposedEmployeeFromList = (list: EmployeeComposed[] = []) => {
  list.forEach((employee) => {
    queryClient.setQueryData(employeeQueries.employee(employee.id).queryKey, employee);
  });
};

export const getItemsWithAffiliationFacilities = async (items: Employee[]) => {
  const dataWithAffiliation = items.filter((i) => i.affiliation);
  const txCentersList = dataWithAffiliation
    .map((i) => [i.affiliation!.txCenterId])
    .flat()
    .filter((i) => i);
  const oposList = dataWithAffiliation
    .map((i) => [i.affiliation!.opoId])
    .flat()
    .filter((i) => i);

  let txCenters = null;
  let opos = null;

  if (txCentersList.length) {
    txCenters = await getTXCentersByIds(txCentersList);
  }

  if (oposList.length) {
    opos = await getOpoOrganizationsByIds(oposList);
  }

  return items.map((c) => {
    const affiliationItem = { ...c };

    if (txCenters) {
      affiliationItem.affiliationTxCenter = txCenters.items.find((h) => h.id === c.affiliation?.txCenterId);
    }

    if (opos) {
      affiliationItem.affiliationOpo = opos.items.find((h) => h.id === c.affiliation?.opoId);
    }

    return affiliationItem;
  });
};

export const getComposedEmployeeData = async (employee: EmployeeAvailabilityData): Promise<EmployeeComposed> => {
  if (!employee) {
    return employee;
  }

  const homeLocation = employee.homeLocation
    ? await queryClient.ensureQueryData(homeLocationQueries.item(employee.homeLocation))
    : undefined;
  const baseLocation = employee.baseLocation
    ? await queryClient.ensureQueryData(hubQueries.item(employee.baseLocation))
    : undefined;
  const closestAirportForDeployment = employee.closestAirportForDeployment
    ? await queryClient.ensureQueryData(airportsQuery.item(`${employee.closestAirportForDeployment}`, 'maoIds'))
    : undefined;
  const affiliationTxCenter = employee.affiliation?.txCenterId
    ? await queryClient.ensureQueryData(txCenterQueries.item(employee.affiliation?.txCenterId)).catch(() => undefined) // Workaround TMDXLP-9462; Should be handled on BE side
    : undefined;
  const affiliationOpo = employee.affiliation?.opoId
    ? await queryClient.ensureQueryData(opoQueries.item(employee.affiliation?.opoId))
    : undefined;

  let currentCSShiftHubs: Hub[] = [];

  if (
    employee.roles?.includes(EmployeeRole.ClinicalSpecialist) &&
    employee.csShiftAvailabilityData?.currentShift?.hubId
  ) {
    const activeShiftHubIds =
      (employee.csShiftAvailabilityData.activeShifts?.map((s) => s.hubId).filter(Boolean) as string[]) ?? [];
    const hubIds = uniq([employee.csShiftAvailabilityData.currentShift.hubId, ...activeShiftHubIds]);
    const hubsList = await Promise.all(hubIds.map((hubId) => queryClient.ensureQueryData(hubQueries.item(hubId))));
    currentCSShiftHubs = hubsList.filter((hub) => !!hub);
  }

  return {
    ...employee,
    homeLocation,
    baseLocation,
    closestAirportForDeployment,
    affiliationTxCenter,
    affiliationOpo,
    ...(employee?.csShiftAvailabilityData && currentCSShiftHubs.length
      ? {
          csShiftAvailabilityData: {
            ...employee.csShiftAvailabilityData,
            currentHubs: currentCSShiftHubs,
          },
        }
      : {}),
  };
};
